// utils/GeolocationUtils.ts


export interface AddressData {
  streetAddress: string;
  zipCode: string;
  place: string;
  country: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
}

export interface GeolocationResult {
  success: boolean;
  data?: AddressData;
  error?: string;
}

export interface GeolocationState {
  loading: boolean;
  error: string | null;
  supported: boolean;
}

export class GeolocationUtils {
  /**
   * Check if geolocation is supported by the browser
   */
  static isSupported(): boolean {
    return 'geolocation' in navigator;
  }

  /**
   * Get current position with timeout and accuracy options
   */
  public static getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!this.isSupported()) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    });
  }

  public static async getCurrentLocationSafe(): Promise<{
    success: boolean;
    coords?: { latitude: number; longitude: number };
    error?: string;
  }> {
    try {
      const position = await this.getCurrentPosition();
      return {
        success: true,
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        }
      };
    } catch (error) {
      let errorMessage = 'Unable to get location';
      
      if (error instanceof GeolocationPositionError) {
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
        }
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }


  /**
   * Reverse geocode coordinates using Google Geocoding API
   */
  private static async reverseGeocodeGoogle(latitude: number, longitude: number): Promise<any> {
    try {
      const apiKey = process.env.GOOGLE_API_KEY;
      
      if (!apiKey) {
        throw new Error('Google API key not found. Please set GOOGLE_API_KEY in your .env file');
      }

      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`
      );

      if (!response.ok) {
        throw new Error(`Google API request failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status !== 'OK') {
        throw new Error(`Google Geocoding API error: ${data.status} - ${data.error_message || 'Unknown error'}`);
      }

      if (!data.results || data.results.length === 0) {
        throw new Error('No address found for the given coordinates');
      }

      return data.results[0]; // Return the first (most accurate) result
    } catch (error) {
      console.error('Google reverse geocoding error:', error);
      throw error;
    }
  }

  /**
   * Fallback reverse geocode using OpenStreetMap Nominatim API
   */
  private static async reverseGeocodeOSM(latitude: number, longitude: number): Promise<any> {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address data from OpenStreetMap');
      }

      const data = await response.json();
      return { address_components: [], osm_data: data.address };
    } catch (error) {
      console.error('OSM reverse geocoding error:', error);
      throw new Error('Failed to get address from coordinates');
    }
  }

  /**
   * Map Google Geocoding API response to standardized format
   */
  private static mapGoogleAddressData(googleResult: any, latitude: number, longitude: number): AddressData {
    const addressComponents = googleResult.address_components || [];
    
    // Helper function to find component by type
    const findComponent = (types: string[]) => {
      return addressComponents.find((component: any) => 
        component.types.some((type: string) => types.includes(type))
      );
    };

    // Extract address components
    const streetNumber = findComponent(['street_number'])?.long_name || '';
    const route = findComponent(['route'])?.long_name || '';
    const locality = findComponent(['locality'])?.long_name || '';
    const adminLevel2 = findComponent(['administrative_area_level_2'])?.long_name || '';
    const adminLevel1 = findComponent(['administrative_area_level_1'])?.long_name || '';
    const country = findComponent(['country'])?.long_name || '';
    const postalCode = findComponent(['postal_code'])?.long_name || '';

    // Build street address
    const streetAddress = [streetNumber, route].filter(Boolean).join(' ');
    
    // Use locality first, then fallback to admin areas for city
    const place = locality || adminLevel2 || adminLevel1 || '';

     return {
    streetAddress,
    zipCode: postalCode,
    place,
    country,
    coordinates: {
      latitude: latitude,
      longitude: longitude
    }
    };
  }

  /**
   * Map OpenStreetMap data to standardized format (fallback)
   */
  private static mapOSMAddressData(osmResult: any, latitude: number, longitude: number): AddressData {
  const addressData = osmResult.osm_data || {};
  
  return {
    streetAddress: [
      addressData.house_number,
      addressData.road || addressData.pedestrian || addressData.path
    ].filter(Boolean).join(' '),
    zipCode: addressData.postcode || '',
    place: addressData.city || addressData.town || addressData.village || addressData.municipality || '',
    country: addressData.country || '',
    coordinates: {
      latitude: latitude,
      longitude: longitude
    }
  };
}

  /**
   * Get user-friendly error message from GeolocationPositionError
   */
  private static getErrorMessage(error: GeolocationPositionError): string {
    switch (error.code) {
      case 1:
        return 'Location access denied. Please enable location services.';
      case 2:
        return 'Location unavailable. Please try again.';
      case 3:
        return 'Location request timed out. Please try again.';
      default:
        return error.message || 'Failed to get location';
    }
  }

  /**
   * Get current location and return formatted address data
   * Main public method to be used by components
   */
  static async getCurrentLocationAddress(): Promise<GeolocationResult> {
    try {
      // Get current position
      const position = await this.getCurrentPosition();
      const { latitude, longitude } = position.coords;

      let formattedAddress: AddressData;

      try {
        // Try Google Geocoding API first
        const googleResult = await this.reverseGeocodeGoogle(latitude, longitude);
        formattedAddress = this.mapGoogleAddressData(googleResult, latitude, longitude);
        console.log('✅ Used Google Geocoding API for address lookup');
      } catch (googleError) {
        console.warn('Google Geocoding API failed, falling back to OpenStreetMap:', googleError);
        
        // Fallback to OpenStreetMap
        const osmResult = await this.reverseGeocodeOSM(latitude, longitude);
        formattedAddress = this.mapOSMAddressData(osmResult, latitude, longitude);
        console.log('✅ Used OpenStreetMap as fallback for address lookup');
      }

      return {
        success: true,
        data: formattedAddress
      };

    } catch (error: any) {
      console.error('Geolocation error:', error);
      
      let errorMessage = 'Failed to get location';
      
      // Handle GeolocationPositionError
      if (error.code !== undefined) {
        errorMessage = this.getErrorMessage(error);
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        error: errorMessage
      };
    }
  }

  /**
   * Create initial geolocation state for components
   */
  static createInitialState(): GeolocationState {
    return {
      loading: false,
      error: null,
      supported: this.isSupported()
    };
  }
}